import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-primary text-primary-foreground border-0 shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.18),0_6px_20px_-8px_hsl(var(--primary)/0.45)] hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.22),0_10px_28px_-10px_hsl(var(--primary)/0.55)]",
        destructive:
          "bg-destructive/90 text-destructive-foreground border-0 shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.18),0_6px_20px_-8px_hsl(var(--destructive)/0.45)] hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.22),0_10px_28px_-10px_hsl(var(--destructive)/0.55)]",
        outline:
          "border-2 border-primary/40 bg-background/40 backdrop-blur-md text-foreground shadow-glass hover:shadow-glass-hover hover:bg-gradient-primary hover:text-primary-foreground hover:border-transparent",
        secondary:
          "bg-gradient-secondary text-secondary-foreground border-0 shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.16),0_6px_20px_-8px_hsl(var(--secondary)/0.4)] hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.20),0_10px_28px_-10px_hsl(var(--secondary)/0.5)]",
        ghost: "hover:bg-muted/50 backdrop-blur-sm hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero:
          "bg-gradient-primary text-primary-foreground font-semibold border-0 shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.22),0_10px_28px_-10px_hsl(var(--primary)/0.55)] hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.28),0_14px_36px_-12px_hsl(var(--primary)/0.65)]",
      },
      size: {
        default: "min-h-[44px] h-11 px-5 py-2",
        sm: "min-h-[36px] h-9 rounded-lg px-3 text-xs",
        lg: "min-h-[48px] h-12 rounded-xl px-6",
        xl: "min-h-[56px] h-14 rounded-xl px-8 text-base",
        icon: "min-h-[44px] h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
